import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export type BlogStatus = "draft" | "review" | "published" | "archived";

export interface Article {
	id: string;
	slug: string;
	title: string;
	excerpt: string | null;
	category: string | null;
	tags: string[];
	status: BlogStatus;
	author: string | null;
	authorId: string | null;
	coverImage: string | null;
	coverAlt: string | null;
	metaTitle: string | null;
	metaDescription: string | null;
	productIds: string[];
	readingMinutes: number;
	publishedAt: string | null;
	scheduledAt: string | null;
	views: number;
	shares: number;
	updatedAt: string;
	createdAt: string;
}

export interface ArticleEvent {
	id: string;
	type: "created" | "updated" | "submitted" | "published" | "unpublished" | "archived";
	actorLabel: string | null;
	note: string | null;
	createdAt: string;
}

export interface ArticleDetail extends Article {
	body: string;
	events: ArticleEvent[];
}

export interface BlogStats {
	published: number;
	review: number;
	draft: number;
	totalViews: number;
}

export type ArticlePayload = Partial<
	Pick<
		Article,
		| "title"
		| "slug"
		| "excerpt"
		| "category"
		| "tags"
		| "coverImage"
		| "coverAlt"
		| "metaTitle"
		| "metaDescription"
		| "productIds"
		| "scheduledAt"
	>
> & { body?: string; authorLabel?: string };

export const blogApi = {
	list: (params?: {
		status?: BlogStatus;
		category?: string;
		search?: string;
		page?: number;
		pageSize?: number;
	}) =>
		unwrap<{ items: Article[]; total: number; page: number; pageSize: number }>(
			api.get<
				ApiEnvelope<{
					items: Article[];
					total: number;
					page: number;
					pageSize: number;
				}>
			>("/admin/blog/articles", { params }),
		),

	stats: () =>
		unwrap<BlogStats>(api.get<ApiEnvelope<BlogStats>>("/admin/blog/stats")),

	categories: () =>
		unwrap<string[]>(api.get<ApiEnvelope<string[]>>("/admin/blog/categories")),

	detail: (id: string) =>
		unwrap<ArticleDetail>(
			api.get<ApiEnvelope<ArticleDetail>>(`/admin/blog/articles/${id}`),
		),

	create: (payload: ArticlePayload & { title: string }) =>
		unwrap<ArticleDetail>(
			api.post<ApiEnvelope<ArticleDetail>>("/admin/blog/articles", payload),
		),

	update: (id: string, payload: ArticlePayload) =>
		unwrap<ArticleDetail>(
			api.patch<ApiEnvelope<ArticleDetail>>(
				`/admin/blog/articles/${id}`,
				payload,
			),
		),

	submit: (id: string) =>
		unwrap<ArticleDetail>(
			api.post<ApiEnvelope<ArticleDetail>>(`/admin/blog/articles/${id}/submit`),
		),

	publish: (id: string, body?: { at?: string; unpublish?: boolean }) =>
		unwrap<ArticleDetail>(
			api.post<ApiEnvelope<ArticleDetail>>(
				`/admin/blog/articles/${id}/publish`,
				body ?? {},
			),
		),

	duplicate: (id: string) =>
		unwrap<ArticleDetail>(
			api.post<ApiEnvelope<ArticleDetail>>(
				`/admin/blog/articles/${id}/duplicate`,
			),
		),

	archive: (id: string) =>
		unwrap<{ archived: boolean }>(
			api.delete<ApiEnvelope<{ archived: boolean }>>(
				`/admin/blog/articles/${id}`,
			),
		),
};

export const BLOG_STATUS_LABELS: Record<
	BlogStatus,
	{ label: string; variant: "success" | "info" | "neutral" | "warning" }
> = {
	published: { label: "Publié", variant: "success" },
	review: { label: "En relecture", variant: "warning" },
	draft: { label: "Brouillon", variant: "neutral" },
	archived: { label: "Archivé", variant: "neutral" },
};
